import React from 'react';
import type { RouteObject } from 'react-router-dom';
import { GuardedRoute } from './GuardedRoute';
import type { GuardedRouteProps } from './GuardedRoute';
import type { PermissionOperationHint } from './access-control/types';

export interface GuardedRouteConfig<ResourceKey = string> {
    path?: string;
    index?: boolean;
    element: React.ReactNode;
    resource?: ResourceKey;
    operation?: PermissionOperationHint;
    children?: GuardedRouteConfig<ResourceKey>[];
}

export interface BuildGuardsOptions<ResourceKey = string> {
    resolvePermission: (
        path: string,
        resource?: ResourceKey,
        operationHint?: PermissionOperationHint
    ) => GuardedRouteProps['allow'];
    mode?: GuardedRouteProps['mode'];
    fallback?: GuardedRouteProps['fallback'];
}

function normalizePath(base: string, segment?: string): string {
    if (!segment || segment === '/') {
        return segment ? segment : base || '/';
    }
    if (segment.startsWith('/')) {
        return segment;
    }
    if (!base || base === '/') {
        return `/${segment}`;
    }
    return `${base.replace(/\/$/, '')}/${segment}`;
}

export function buildGuardedRoutes<ResourceKey = string>(
    routes: GuardedRouteConfig<ResourceKey>[],
    options: BuildGuardsOptions<ResourceKey>,
    basePath = ''
): RouteObject[] {
    return routes.map(({ path, index, element, resource, operation, children }) => {
        const fullPath = normalizePath(basePath, path);
        const allow = options.resolvePermission(fullPath, resource, operation);

        const guardedElement = allow
            ? React.createElement<GuardedRouteProps>(GuardedRoute, {
                  allow,
                  mode: options.mode,
                  fallback: options.fallback,
                  children: element,
              })
            : element;

        const childRoutes = children ? buildGuardedRoutes(children, options, fullPath) : undefined;

        const routeObject: RouteObject = index
            ? {
                  index: true,
                  element: guardedElement,
              }
            : {
                  element: guardedElement,
                  ...(typeof path === 'string' ? { path } : {}),
                  ...(childRoutes ? { children: childRoutes } : {}),
              };

        return routeObject;
    });
}
